package com.pincer.app.viewmodel

import com.pincer.core.Logger
import com.pincer.core.PincerException
import com.pincer.app.I18n
import kotlin.js.JsName

typealias Init = Node.() -> Unit

/*
The Pincer ViewModel is just a tree of simple text nodes, designed to be easy to consume by assistive technologies.

Each node has a key which needs to be unique within its parent, giving globally unique compound keys from each lineage.

Nodes can be qualified to show in specific ways and also to be one of a range of controls.
*/

public enum class ShowAs() {
    Loading,
    Warning,
    Problem,
    HelpText,
}

/*
How much a node matters where it is, in terms of meaning rather than layout. Each renderer decides what that looks
like: a designed page makes primary nodes big and puts secondary ones at the edges, a phone might leave secondary
ones for a menu, and the outline an agent reads marks primary lines. Prominence is a hint, never permission: a
secondary node is still in the tree, and anyone can use its controls. See docs/design_notes/clients.md.
*/
public enum class Prominence() {
    Primary,     // the gist here, and the one or two things to do next
    Normal,
    Secondary,   // there if you look for it: settings, history, advanced options
}

public enum class ControlType() {
    Button,
    Input,
    Multiline,
    Select,
    Checkbox,
}

/*
I looked hard at modelling this in different OO ways (inheritance, composition):

- Dedicated subclasses for ShowAs + Controls
- ShowAs stays a field, Controls as subclasses
- Control as an independent class included as an optional field on the Node

Nothing added enough merit to warrant the extra complexity.
*/
class Node(
    @JsName("key")            val key:            String,
    @JsName("wording")        val wording:        String,
    @JsName("parent")         val parent:         Node?,
    @JsName("showAs")         val showAs:         ShowAs?,
    @JsName("controlType")    val controlType:    ControlType?,
    @JsName("controlChoices") val controlChoices: Map<String, String>?,
                              val action:         Action?,
    @JsName("rich")           val rich:           Rich? = null,
    @JsName("prominence")     val prominence:     Prominence = Prominence.Normal,
    @JsName("summary")        val summary:        String? = null,
    @JsName("open")           val open:           Boolean? = null,

) {

    init {
        if ((controlType == null) != (action == null)) throw PincerException("A control needs an action, and an action needs a control: $key")
        if ((controlType == ControlType.Select) != (controlChoices != null)) throw PincerException("Choices go with a select, and only a select: $key")
        if (open != null && controlType != null) throw PincerException("A control can't open and close: $key")
    }

    /*
    Nodes that open on request. A node with `open` set (true or false) can be opened and closed, and the state is
    kept by the app, not the renderer, so every renderer and agent sees the same thing. Its first child is always a
    checkbox keyed "open" that sets it (Action.SetOpen). While it's closed that's its only child: what's inside isn't
    built, isn't sent to a renderer and isn't in an agent's outline, and its controls can't be used until someone
    opens it, as on screen. Its `summary`, if any, stands in for what's inside ("12 earlier messages").

    A renderer that knows about `open` draws the node as a disclosure and doesn't draw the checkbox separately; one
    that doesn't still works, with a plain checkbox. See docs/design_notes/clients.md.
    */
    @JsName("openControl")
    fun openControl(): Node? = if (open == null) null else child(OPEN_KEY)

    /*
    Checks a value someone wants to give this control, returning a problem or null. A value isn't checked as
    data here (that's the action's job, with the same validation the tree shows), only that it fits the control:
    a button takes nothing, a select one of its choices, a checkbox true or false.
    */
    fun problemWithValue(value: String?): String? = when (controlType) {
        null                  -> "$key isn't a control"
        ControlType.Button    -> if (value.isNullOrEmpty()) null else "$key is a button, it takes no value"
        ControlType.Input,
        ControlType.Multiline -> if (value == null) "$key needs a value" else null
        ControlType.Select    -> if (value != null && controlChoices!!.containsKey(value)) null else "$key needs one of: ${controlChoices!!.keys.joinToString(", ")}"
        ControlType.Checkbox  -> if (value == "true" || value == "false") null else "$key needs true or false"
    }

    @JsName("wordingSafe")
    fun wordingSafe() = wording.replace("<","&lt;").replace(">","&gt;")

    @JsName("compoundKey")
    fun compoundKey(): String {
        if (parent == null) return cleanKey(key)
        return parent.compoundKeyOf(key)
    }

    // The compound key a child with this key has (or would have)
    fun compoundKeyOf(childKey: String) = compoundKey() + "-" + cleanKey(childKey)

    @JsName("children")
    val children = mutableListOf<Node>()

    @JsName("child")
    fun child(key: String): Node? {
        for (child in children) {
            if (child.key == key) return child
        }
        return null
    }

    @JsName("eachChild")
    fun eachChild(f: (Node) -> Unit) {
        for (child in children) f(child)
    }

    @JsName("eachControlChoice")
    fun eachControlChoice(f: (String,String) -> Unit) {
        controlChoices!!.forEach { f(it.key, it.value) }
    }

    @JsName("numberOfChildren")
    fun numberOfChildren() = children.size

    @JsName("hasChildren")
    fun hasChildren() = children.size > 0

    // Finds a node by its compound key, or by the end of one (whole keys only) when that's unique in the tree
    @JsName("find")
    fun find(keyOrSuffix: String): Node? {
        val matches = mutableListOf<Node>()
        walk { if (it.compoundKey() == keyOrSuffix || it.compoundKey().endsWith("-$keyOrSuffix")) matches.add(it) }
        return matches.find { it.compoundKey() == keyOrSuffix } ?: matches.singleOrNull()
    }

    fun walk(f: (Node) -> Unit) {
        f(this)
        for (child in children) child.walk(f)
    }

    @JsName("getPreviousSibling")
    fun getPreviousSibling(): Node? {
        if (parent == null) return null
        val i = parent.children.indexOf(this)
        if (i == 0) return null
        return parent.children[i-1]
    }

    fun node(
        key:            String,
        wording:        String = I18n.translate(key),
        showAs:         ShowAs? = null,
        controlType:    ControlType? = null,
        controlChoices: Map<String, String>? = null,
        action:         Action? = null,
        rich:           Rich? = null,
        prominence:     Prominence = Prominence.Normal,
        summary:        String? = null,
        open:           Boolean? = null,
        init:           Init = {},
    ): Node {
        for (child in children) { if (child.key == key) throw PincerException("Sibiling view model nodes with the same key $key")}
        val n = Node(key = key, wording = wording, parent = this, showAs = showAs, controlType = controlType, controlChoices = controlChoices, action = action, rich = rich, prominence = prominence, summary = summary, open = open)
        if (open != null) n.checkbox(key = OPEN_KEY, action = Action.SetOpen(n.compoundKey()), wording = open.toString())
        if (open != false) n.init()
        children.add(n)
        return n
    }

    fun  problem(tokenOrWording: String, init: Init = {}) = node(key = "problem",  wording = I18n.translate(tokenOrWording), showAs = ShowAs.Problem,  init = init)
    fun  loading(tokenOrWording: String, init: Init = {}) = node(key = "loading",  wording = I18n.translate(tokenOrWording), showAs = ShowAs.Loading,  init = init)
    fun helpText(tokenOrWording: String, init: Init = {}) = node(key = "helpText", wording = I18n.translate(tokenOrWording), showAs = ShowAs.HelpText, init = init)

    private fun problemAs(problem: String?) = if (problem == null) null else ShowAs.Problem

    fun button(key: String, action: Action, wording: String = I18n.translate(key))
        = node(key = key, wording = wording, controlType = ControlType.Button, action = action)

    fun input(key: String, action: Action, wording: String, problem: String?, rich: Rich? = null)
        = node(key = key, wording = wording, controlType = ControlType.Input, action = action, showAs = problemAs(problem), rich = rich)

    fun multiline(key: String, action: Action, wording: String, problem: String?)
        = node(key = key, wording = wording, controlType = ControlType.Multiline, action = action, showAs = problemAs(problem))

    fun select(key: String, action: Action, wording: String, controlChoices: Map<String, String>, problem: String? = null)
        = node(key = key, wording = wording, controlType = ControlType.Select, controlChoices = controlChoices, action = action, showAs = problemAs(problem))

    fun checkbox(key: String, action: Action, wording: String, problem: String? = null)
        = node(key = key, wording = wording, controlType = ControlType.Checkbox, action = action, showAs = problemAs(problem))

    fun possibleProblem(key: String?) {
        if (key == null) return
        problem(key)
    }

    /*
    This was from when we had a pruning version going. It worked like an assertion that either
     - found the child extant and removed it from the to-be-deleted list
     - created the missing child

    fun hasChild(key: String, wording: String? = null, sortToken: String? = null): Node {
        patrolCount++
        val build = prune[key] ?: Node(key = key, parent = this)
        build.sortToken = sortToken ?: patrolCount.toString().padStart(10, '0')
        build.wording = if (wording == null) I18n.translate(key) else escape(wording)
        if (prune[key] == null) {
            children.add(build)
        }
        else {
            prune.remove(key)
        }
        return build
    }
    */

}

const val OPEN_KEY = "open"

private fun cleanKey(key: String) = key.replace("-","").replace("/","").replace("+","").replace(":","")

fun root(init: Node.() -> Unit): Node {
    val node = Node(key = "root", wording = I18n.translate("root"), parent = null, showAs = null, controlType = null, controlChoices = null, action = null)
    node.init()
    return node
}
